import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { TimerReset as KeyReset, Loader2, ArrowLeft } from 'lucide-react';

export function ResetPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'error' | 'success'>('error');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) throw error;

      setMessageType('success');
      setMessage('Check your email for the password reset link.');
      setSubmitted(true);
    } catch (error: any) {
      setMessageType('error');
      setMessage(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-teal/5 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <div className="bg-brand-teal/10 p-3 rounded-full">
            <KeyReset className="w-8 h-8 text-brand-teal" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-center text-brand-teal mb-2">
          Reset Your Password
        </h2>
        <p className="text-center text-gray-600 text-sm mb-8">
          Enter your email address and we'll send you instructions to reset your password.
        </p>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                placeholder="you@company.com"
              />
            </div>

            {message && (
              <p className={`text-sm ${
                messageType === 'error' ? 'text-red-600' : 'text-green-600'
              } text-center`}>
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-orange text-white py-2 px-4 rounded-lg hover:bg-brand-orange-light focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                  Sending...
                </span>
              ) : (
                'Send Reset Instructions'
              )}
            </button>
          </form>
        ) : (
          <div className="text-center">
            <div className="rounded-lg bg-green-50 p-4 mb-6">
              <p className="text-green-800">
                We've sent you an email with instructions to reset your password.
                Please check your inbox and spam folder.
              </p>
            </div>
            <button
              onClick={() => {
                setSubmitted(false);
                setEmail('');
                setMessage('');
              }}
              className="text-brand-orange hover:text-brand-orange-light transition-colors"
            >
              Try a different email
            </button>
          </div>
        )}

        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-brand-orange hover:text-brand-orange-light transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}